import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, FileText, Loader2, Calendar, MapPin, Building } from 'lucide-react';
import { api } from '../App';
import Chip from '../components/Chip';
import Tabs from '@shared/ui/Tabs';
import PageShell from '../components/PageShell';
import { StandardTableHeader } from '../components/StandardTable';
import StatCard, { StatCardRow } from '../components/StatCard';
import EmptyState from '../components/EmptyState';
import { formatDate } from '../utils/dateUtils';

interface ClientData {
  id: string;
  name: string;
  zip: string;
  total_lobbyists: number;
  total_bills: number;
  first_lobbying_date: string;
  last_activity_date: string;
  created_at: string;
  updated_at: string;
  lobbyists: Array<{
    id: string;
    lobbyist_name: string;
    lobbyist_firm_name: string;
    status: string;
    bill_count: number;
    first_representation_date: string;
    last_activity_date: string;
  }>;
  bills: Array<{
    id: string;
    number: string;
    title: string;
    session: string;
    position: string;
    lobbying_start_date: string;
    lobbyist_name: string;
    lobbyist_firm_name: string;
  }>;
  promotional_content: any[];
}

interface ClientDetailResponse {
  success: boolean;
  data: ClientData;
  error?: string;
}

const ClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [clientData, setClientData] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'lobbyists' | 'bills'>('lobbyists');

  useEffect(() => {
    if (id) {
      fetchClientDetail(id);
    }
  }, [id]);

  const fetchClientDetail = async (clientId: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/lobbying/clients/${clientId}`);
      const data: ClientDetailResponse = response.data;
      
      if (data.success) {
        setClientData(data.data);
      } else {
        setError(data.error || 'Failed to load client details');
      }
    } catch (error: any) {
      console.error('Error fetching client details:', error);
      if (error.response?.status === 404) {
        setError('Client not found');
      } else {
        setError('Failed to load client details');
      }
    } finally {
      setLoading(false);
    }
  };


  const handleLobbyistClick = (lobbyistId: string) => {
    navigate(`/lobbyists/${lobbyistId}?returnTo=client-detail&returnId=${id}`);
  };

  const handleBillClick = (billId: string) => {
    navigate(`/legislation/${billId}?returnTo=client-detail&returnId=${id}`);
  };

  const tabs = [
    { 
      id: 'lobbyists' as const, 
      label: 'Lobbyists', 
      icon: Users,
      count: clientData?.total_lobbyists || 0
    },
    { 
      id: 'bills' as const, 
      label: 'Bills', 
      icon: FileText,
      count: clientData?.total_bills || 0
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !clientData) {
    return (
      <EmptyState tone="error" title={error || 'Client not found'} />
    );
  }

  return (
    <PageShell title={clientData.name} description="Client" backUrl="/lobbying">
      {/* Client Information */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          {/* Main Info */}
          <div className="flex-1">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Location */}
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-text-secondary dark:text-text-dark-secondary mt-0.5" />
                  <div>
                    <p className="font-medium text-text-primary dark:text-text-dark-primary">Location</p>
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      {clientData.zip ? `Zip Code: ${clientData.zip}` : 'No location on file'}
                    </p>
                  </div>
                </div>

                {/* Activity Summary */}
                <div className="flex items-start gap-3">
                  <Building className="w-5 h-5 text-text-secondary dark:text-text-dark-secondary mt-0.5" />
                  <div>
                    <p className="font-medium text-text-primary dark:text-text-dark-primary">Activity Summary</p>
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      {clientData.total_lobbyists} lobbyist{clientData.total_lobbyists !== 1 ? 's' : ''}, 
                      {' '}{clientData.total_bills} bill{clientData.total_bills !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {/* Date Range */}
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-text-secondary dark:text-text-dark-secondary mt-0.5" />
                  <div>
                    <p className="font-medium text-text-primary dark:text-text-dark-primary">Lobbying Period</p>
                      {clientData.first_lobbying_date ? (
                        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                          {formatDate(clientData.first_lobbying_date)} - {formatDate(clientData.last_activity_date)}
                        </p>
                      ) : (
                        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">No lobbying activity</p>
                      )}
                  </div>
                </div>
              </div>

            {/* Quick Stats */}
            <StatCardRow className="max-w-xs">
              <StatCard value={clientData.total_lobbyists} label="Lobbyists" variant="info" />
              <StatCard value={clientData.total_bills} label="Bills" variant="info" />
            </StatCardRow>
          </div>
        </div>
      </div>  {/* End card */}

      {/* Tab Navigation */}
      <div className="mt-6">
        <Tabs
          level="panel"
          ariaLabel="Client sections"
          tabs={tabs.map((tab) => ({
            id: tab.id,
            label: tab.label,
            icon: tab.icon,
            // The count was baked into the label string; `badge` is the slot for it.
            badge: <span className="tabular-nums">({tab.count})</span>,
          }))}
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as typeof activeTab)}
        />
      </div>

      {/* Tab Content */}
      {activeTab === 'lobbyists' && (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
                <StandardTableHeader>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                      Lobbyist
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                      Firm
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                      Bills
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                      Period
                    </th>
                  </tr>
                </StandardTableHeader>
                <tbody className="divide-y divide-border-light dark:divide-border-dark">
                  {clientData.lobbyists && clientData.lobbyists.length > 0 ? clientData.lobbyists.map((lobbyist: any) => (
                    <tr 
                      key={lobbyist.id}
                      className="hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                    >
                      <td className="px-3 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleLobbyistClick(lobbyist.id)}
                          className="text-left"
                        >
                          <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                            {lobbyist.lobbyist_name}
                          </div>
                        </button>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-text-secondary dark:text-text-dark-secondary">
                        {lobbyist.lobbyist_firm_name || '-'}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <Chip 
                          label={lobbyist.status || 'Active'} 
                          variant={lobbyist.status === 'Active' ? 'success' : 'secondary'}
                        />
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-text-primary dark:text-text-dark-primary">
                        {lobbyist.bill_count}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-text-secondary dark:text-text-dark-secondary">
                        {formatDate(lobbyist.first_representation_date)} - {formatDate(lobbyist.last_activity_date)}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-text-secondary dark:text-text-dark-secondary">
                        No lobbyists found for this client
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
        </div>
      )}

      {activeTab === 'bills' && (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
                <StandardTableHeader>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider w-auto sm:w-48">
                      Bill
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                      Position
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                      Lobbyist
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                      Started
                    </th>
                  </tr>
                </StandardTableHeader>
                <tbody className="divide-y divide-border-light dark:divide-border-dark">
                  {clientData.bills && clientData.bills.length > 0 ? clientData.bills.map((bill) => (
                    <tr 
                      key={bill.id}
                      className="hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                    >
                      <td className="px-3 py-4">
                        <button
                          onClick={() => handleBillClick(bill.id)}
                          className="text-left"
                        >
                          <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                            {bill.number}
                          </div>
                          <div className="text-xs text-text-secondary dark:text-text-dark-secondary line-clamp-2 mt-1">
                            {bill.title}
                          </div>
                        </button>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <Chip 
                          label={bill.session} 
                          variant="secondary"
                        />
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-text-primary dark:text-text-dark-primary">
                        {bill.position || '-'}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-text-secondary dark:text-text-dark-secondary">
                        {bill.lobbyist_name} {bill.lobbyist_firm_name && `(${bill.lobbyist_firm_name})`}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-text-secondary dark:text-text-dark-secondary">
                        {formatDate(bill.lobbying_start_date)}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-text-secondary dark:text-text-dark-secondary">
                        No bills found for this client
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
        </div>
      )}
    </PageShell>
  );
};

export default ClientDetailPage;
